import {
	PermissionDependencies,
	ReverseDependencies,
} from '../../../core/domain/permission-types';
import { PermissionConfigItemEntity } from './permission-config-item.entity';

export type PermissionConfig = {
	permission: string;
	isDisabled: boolean;
	toggled: boolean;
};

interface IPermissionConfigListEntity {
	availablePermissions: Map<string, PermissionConfig>;
	elements: Map<string, PermissionConfigItemEntity>;
}

const entityDefaults: IPermissionConfigListEntity = {
	availablePermissions: new Map(),
	elements: new Map(),
};

export class PermissionConfigListEntity implements IPermissionConfigListEntity {
	public elements = entityDefaults.elements;
	public availablePermissions = entityDefaults.availablePermissions;
	public activePermissions: Array<string> = [];

	public static build(
		availablePermissions: Array<PermissionConfig>
	): PermissionConfigListEntity {
		const inst = new PermissionConfigListEntity();
		const cols = availablePermissions.reduce((acc, curr) => {
			acc.push([curr.permission, curr]);
			return acc;
		}, new Array<[string, PermissionConfig]>());
		inst.availablePermissions = new Map(cols);
		inst.activePermissions = availablePermissions
			.filter((config) => !config.isDisabled)
			.map((config) => config.permission);

		return inst;
	}

	private constructor() {}

	public setElements(
		elems: Map<string, PermissionConfigItemEntity>
	): PermissionConfigListEntity {
		this.elements = elems;
		return this.checkIfAllSelected();
	}

	/**
	 * Met à jour un élément de la liste et recalcule
	 * l'état des toggles des permissions disponibles.
	 */
	public updateElement(
		key: string,
		element: PermissionConfigItemEntity
	): PermissionConfigListEntity {
		this.elements.set(key, element);

		/**
		 * Recalcule l'état des toggles pour chaque permission active.
		 *
		 * Pour chaque permission non désactivée, le toggle est activé
		 * si tous les éléments possèdent cette permission.
		 */
		[...this.availablePermissions.values()]
			.filter((per) => !per.isDisabled)
			.forEach((config) => {
				config.toggled = this.hasAllSelected(config.permission);
				this.availablePermissions.set(config.permission, config);
			});
		return this;
	}

	/**
	 * Active ou désactive une permission pour l'ensemble des éléments,
	 * en tenant compte des dépendances associées.
	 */
	public updateAllElements(
		checked: boolean,
		permission: string
	): PermissionConfigListEntity {
		const dependencies = checked
			? PermissionDependencies[permission] ?? []
			: ReverseDependencies[permission] ?? [];

		const permissionsToUpdate = [permission, ...dependencies];

		/**
		 * Met à jour uniquement les éléments concernés
		 */
		[...this.elements.values()]
			.filter((el) =>
				checked
					? !el.permissions.includes(permission)
					: el.permissions.includes(permission)
			)
			.forEach((elem) => {
				const updatedPermissions = checked
					? elem.permissions.union(permissionsToUpdate)
					: elem.permissions.subtract(permissionsToUpdate);
				elem.updatePermissions(updatedPermissions);
				elem.updateAllToggled(
					elem.permissions.size === this.activePermissions.length
				);
			});

		/**
		 * Met à jour l'état des toggles des permissions concernées
		 * dans la liste des permissions disponibles.
		 */
		permissionsToUpdate.forEach((p) => {
			const selectedPermission = this.availablePermissions.get(p);

			if (!selectedPermission) {
				return;
			}
			this.availablePermissions.set(p, {
				...selectedPermission,
				toggled: checked,
			});
		});

		return this;
	}

	public checkIfAllSelected(): PermissionConfigListEntity {
		this.availablePermissions.forEach((config, permission) => {
			config.toggled = this.hasAllSelected(permission);
			this.availablePermissions.set(permission, config);
		});

		this.elements.forEach((elem) => {
			elem.updateAllToggled(
				elem.permissions.size === this.activePermissions.length
			);
		});

		return this;
	}

	private hasAllSelected(permission: string): boolean {
		if (this.elements.size === 0) {
			return false;
		}
		for (const elem of this.elements.values()) {
			if (!elem.permissions.includes(permission)) {
				return false;
			}
		}
		return true;
	}
}
